/**
 * VueVisibility: Vue plugin - v1.0.0 (c) 2020; aburai
 *
 * @version 1.0.0
 * @author André Bunse (aburai@github.com) <andre.bunse@gmail.com>
 *
 * inspired by visibility.core.js
 * 2011 Andrey “A.I.” Sitnik <andrey@sitnik.ru>,
 * sponsored by Evil Martians.
 */
//  __     ___     _ _     _ _ _ _
//  \ \   / (_)___(_) |__ (_) (_) |_ _   _
//   \ \ / /| / __| | '_ \| | | | __| | | |
//    \ V / | \__ \ | |_) | | | | |_| |_| |
//     \_/  |_|___/_|_.__/|_|_|_|\__|\__, |
//                                   |___/
const _id = Symbol('visibility-change-id')

let hidden
let visibilityChange
let lastId = -1

if (typeof document.hidden !== 'undefined') { // Opera 12.10 and Firefox 18 and later support
  hidden = 'hidden'
  visibilityChange = 'visibilitychange'
}
else if (typeof document.msHidden !== 'undefined') {
  hidden = 'msHidden'
  visibilityChange = 'msvisibilitychange'
}
else if (typeof document.webkitHidden !== 'undefined') {
  hidden = 'webkitHidden'
  visibilityChange = 'webkitvisibilitychange'
}
else if (typeof document.mozHidden !== 'undefined') {
  hidden = 'mozHidden'
  visibilityChange = 'mozvisibilitychange'
}

const _callbacks = {}
const _doc = document
let _init

/**
 * Listener for `visibilitychange` event.
 * @param {Event} event event object
 */
function _change(event) {
  for (const i in _callbacks) {
    _callbacks[i].call(_doc, event, _doc[hidden])
  }
}

/**
 * Set listener for `visibilitychange` event.
 */
function _listen() {
  /* istanbul ignore if */
  if (_init) return

  const listener = function () {
    _change.apply(VueVisibility, arguments)
  }
  document.addEventListener(visibilityChange, listener)

  _init = true
}

const _visibility = {
  /**
   * @param {Function} callback
   *  - {Event} event
   *  - {Boolean} hidden
   */
  change(callback) {
    if (!this.isSupported()) return false

    lastId += 1
    const number = lastId
    _callbacks[number] = callback
    _listen()
    return number
  },

  /**
   * Remove `change` listener by it ID.
   *
   * @param {Number} id
   *
   * @example
   * var id = this.$visibility.change(function(e, state) {
   *     firstChangeCallback()
   *     this.$visibility.unbind(id)
   * });
   */
  unbind(id) {
    delete _callbacks[id]
  },

  /**
   * Return true if browser support Page Visibility API.
   */
  isSupported() {
    return hidden !== undefined;
  },

  /**
   * Return true if page now isn’t visible to user.
   */
  hidden() {
    return _doc[hidden];
  }
}

// VueVisibility allow you to know, that your web page is in the background
// tab and thus not visible to the user. This library is wrap under
// Page Visibility API. It fix problems with different vendor prefixes and
// add high-level useful functions.
const VueVisibility = {
  install(Vue) {
    Vue.prototype.$visibility = _visibility
    Vue.directive('visibility-change', {
      bind(el, {value}) {
        if (typeof value === 'function') {
          el[_id] = _visibility.change((evt, hidden) => {
            value(evt, hidden)
          })
        }
        else {
          console.error('You need bind a callback function for v-visibility-change')
        }
      },
      unbind(el) {
        _visibility.unbind(el[_id])
      }
    })
  }
}

export default VueVisibility
